import React, { useState, useEffect } from 'react';
import { createPortal } from 'react-dom';
import { FaPlus, FaSearch, FaTrash, FaEdit, FaCopy, FaEye, FaPaperPlane, FaTimes } from 'react-icons/fa';
import axios from 'axios';
import ReactQuill from 'react-quill';
import 'react-quill/dist/quill.snow.css';
import { useToast } from '../../context/ToastContext';
import SkeletonLoader from '../../components/ui/SkeletonLoader';
import PageLayout from '../../components/ui/PageLayout';
import PageHeader from '../../components/ui/PageHeader';
import EmailCampaignsNav from '../../components/ui/EmailCampaignsNav';
import FilterCard from '../../components/ui/FilterCard';
import EmptyState from '../../components/ui/EmptyState';

const EmailTemplates = () => {
  const [templates, setTemplates] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState('');
  const [showModal, setShowModal] = useState(false);
  const [showPreview, setShowPreview] = useState(false);
  const [previewData, setPreviewData] = useState(null);
  const [editingTemplate, setEditingTemplate] = useState(null);
  const [formData, setFormData] = useState({
    templateName: '',
    subject: '',
    body: '',
    category: 'custom',
    type: 'campaign'
  });
  const { success: toastSuccess, error: toastError } = useToast();

  useEffect(() => {
    fetchTemplates();
  }, []);

  const fetchTemplates = async () => {
    try {
      const token = localStorage.getItem('adminToken');
      const res = await axios.get(`${process.env.REACT_APP_API_BASE_URL}/api/campaigns/templates`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      if (res.data.success) {
        setTemplates(res.data.data);
      }
    } catch (err) {
      toastError('Failed to fetch templates');
    } finally {
      setLoading(false);
    }
  };

  const handleOpenModal = (template = null) => {
    if (template) {
      setEditingTemplate(template);
      setFormData({
        templateName: template.templateName,
        subject: template.subject,
        body: template.body,
        category: template.category,
        type: template.type
      });
    } else {
      setEditingTemplate(null);
      setFormData({
        templateName: '',
        subject: '',
        body: '',
        category: 'custom',
        type: 'campaign'
      });
    }
    setShowModal(true);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    const token = localStorage.getItem('adminToken');
    try {
      if (editingTemplate) {
        await axios.put(`${process.env.REACT_APP_API_BASE_URL}/api/campaigns/templates/${editingTemplate._id}`, formData, {
          headers: { Authorization: `Bearer ${token}` }
        });
        toastSuccess('Template updated');
      } else {
        await axios.post(`${process.env.REACT_APP_API_BASE_URL}/api/campaigns/templates`, formData, {
          headers: { Authorization: `Bearer ${token}` }
        });
        toastSuccess('Template created');
      }
      setShowModal(false);
      fetchTemplates();
    } catch (err) {
      toastError(err.response?.data?.message || 'Action failed');
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Delete this template?')) return;
    const token = localStorage.getItem('adminToken');
    try {
      await axios.delete(`${process.env.REACT_APP_API_BASE_URL}/api/campaigns/templates/${id}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toastSuccess('Template deleted');
      fetchTemplates();
    } catch (err) {
      toastError('Delete failed');
    }
  };

  const handleDuplicate = async (id) => {
    const token = localStorage.getItem('adminToken');
    try {
      await axios.post(`${process.env.REACT_APP_API_BASE_URL}/api/campaigns/templates/${id}/duplicate`, {}, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toastSuccess('Template duplicated');
      fetchTemplates();
    } catch (err) {
      toastError('Duplication failed');
    }
  };

  const handleTestEmail = async (templateId) => {
    const email = window.prompt('Enter email address to send test email:');
    if (!email) return;
    const token = localStorage.getItem('adminToken');
    try {
      await axios.post(`${process.env.REACT_APP_API_BASE_URL}/api/campaigns/send-test`, {
        templateId,
        targetEmail: email
      }, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toastSuccess('Test email sent');
    } catch (err) {
      toastError('Failed to send test email');
    }
  };

  const filteredTemplates = templates.filter(t => 
    t.templateName.toLowerCase().includes(searchQuery.toLowerCase()) ||
    t.subject.toLowerCase().includes(searchQuery.toLowerCase())
  );

  const quillModules = {
    toolbar: [
      [{ 'header': [1, 2, false] }],
      ['bold', 'italic', 'underline', 'strike', 'blockquote'],
      [{ 'list': 'ordered' }, { 'list': 'bullet' }, { 'indent': '-1' }, { 'indent': '+1' }],
      ['link', 'image', 'color'],
      ['clean'],
      ['code-block']
    ],
  };

  return (
    <PageLayout>
      <PageHeader
        title="Email Campaigns"
        description="Design and manage reusable email layouts"
        icon={FaPaperPlane}
        actions={
          <button onClick={() => handleOpenModal()} className="btn-premium flex items-center gap-2">
            <FaPlus className="w-4 h-4" /> New Template
          </button>
        }
      />

      <EmailCampaignsNav />

      <FilterCard>
        <div className="relative w-full">
          <FaSearch className="absolute left-4 top-1/2 -translate-y-1/2 text-gray-400 w-4 h-4" />
          <input
            type="text"
            placeholder="Search templates..."
            className="input-modern pl-11 py-2.5 text-sm"
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
          />
        </div>
      </FilterCard>

      {loading ? (
        <SkeletonLoader variant="grid" items={6} />
      ) : filteredTemplates.length === 0 ? (
        <EmptyState preset="create" title="No Templates Yet" description="Create your first email template to get started." />
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {filteredTemplates.map(template => (
            <div key={template._id} className="bg-white dark:bg-dark-800 rounded-2xl p-5 shadow-card border border-gray-100 dark:border-dark-700 group hover:shadow-lg transition-shadow">
              <div className="flex justify-between items-start mb-4">
                <div className={`p-3 rounded-xl bg-primary-100 dark:bg-primary-900/30 text-primary-600`}>
                  <FaPaperPlane size={24} />
                </div>
                <div className="flex gap-1">
                  <button onClick={() => { setPreviewData(template); setShowPreview(true); }} className="p-2 text-gray-400 hover:text-primary-500 transition-colors" title="Preview"><FaEye /></button>
                  <button onClick={() => handleDuplicate(template._id)} className="p-2 text-gray-400 hover:text-blue-500 transition-colors" title="Duplicate"><FaCopy /></button>
                  <button onClick={() => handleOpenModal(template)} className="p-2 text-gray-400 hover:text-green-500 transition-colors" title="Edit"><FaEdit /></button>
                  <button onClick={() => handleDelete(template._id)} className="p-2 text-gray-400 hover:text-red-500 transition-colors" title="Delete"><FaTrash /></button>
                </div>
              </div>
              <h3 className="text-lg font-bold dark:text-white truncate">{template.templateName}</h3>
              <p className="text-sm text-gray-500 truncate mb-4">{template.subject}</p>
              <div className="flex items-center justify-between mt-auto pt-4 border-t border-gray-100 dark:border-dark-700">
                <span className="badge badge-info">{template.category}</span>
                <button 
                  onClick={() => handleTestEmail(template._id)}
                  className="text-xs font-semibold uppercase text-primary-600 hover:underline"
                >
                  Send Test
                </button>
              </div>
            </div>
          ))}
        </div>
      )}

      {showModal && createPortal(
        <div className="modal-overlay">
          <div className="modal-content !max-w-4xl">
            <div className="p-6 border-b border-gray-100 dark:border-dark-700 flex justify-between items-center">
              <h2 className="text-xl font-bold dark:text-white">{editingTemplate ? 'Edit Template' : 'New Template'}</h2>
              <button onClick={() => setShowModal(false)} className="text-gray-400 hover:text-gray-600"><FaTimes /></button>
            </div>
            <div className="p-6 space-y-4 overflow-y-auto max-h-[80vh]">
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-1">
                  <label className="text-[10px] font-black uppercase text-gray-500">Template Name</label>
                  <input 
                    className="input-modern"
                    value={formData.templateName}
                    onChange={(e) => setFormData({...formData, templateName: e.target.value})}
                  />
                </div>
                <div className="space-y-1">
                  <label className="text-[10px] font-black uppercase text-gray-500">Category</label>
                  <select 
                    className="input-modern"
                    value={formData.category}
                    onChange={(e) => setFormData({...formData, category: e.target.value})}
                  >
                    <option value="greeting">Greeting</option>
                    <option value="reminder">Reminder</option>
                    <option value="promotional">Promotional</option>
                    <option value="tax">Tax</option>
                    <option value="custom">Custom</option>
                  </select>
                </div>
              </div>
              <div className="space-y-1">
                <label className="text-[10px] font-black uppercase text-gray-500">Subject Line</label>
                <input 
                  className="input-modern"
                  value={formData.subject}
                  onChange={(e) => setFormData({...formData, subject: e.target.value})}
                />
              </div>
              <div className="space-y-1">
                <div className="flex justify-between items-center">
                  <label className="text-[10px] font-black uppercase text-gray-500">Email Body (HTML)</label>
                  <div className="flex gap-2">
                    {['{{name}}', '{{email}}', '{{service}}', '{{due_date}}'].map(v => (
                      <button 
                        key={v}
                        onClick={() => setFormData({...formData, body: formData.body + v})}
                        className="text-[10px] font-bold bg-gray-100 dark:bg-dark-700 px-2 py-1 rounded hover:bg-primary-50"
                      >
                        {v}
                      </button>
                    ))}
                  </div>
                </div>
                <div className="bg-white dark:bg-dark-800 rounded-xl overflow-hidden mt-1 border border-gray-200 dark:border-dark-600">
                  <ReactQuill 
                    theme="snow" 
                    value={formData.body}
                    onChange={(val) => setFormData({...formData, body: val})}
                    modules={quillModules}
                    className="h-80 pb-12"
                  />
                </div>
              </div>
            </div>
            <div className="p-6 border-t border-gray-100 dark:border-dark-700 flex gap-3">
              <button onClick={() => setShowModal(false)} className="flex-1 px-4 py-2 border border-gray-200 dark:border-dark-600 rounded-lg font-bold text-gray-600">Cancel</button>
              <button onClick={handleSubmit} className="flex-1 btn-premium">Save Template</button>
            </div>
          </div>
        </div>,
        document.body
      )}

      {showPreview && previewData && createPortal(
        <div className="modal-overlay" onClick={() => setShowPreview(false)}>
          <div className="modal-content !max-w-2xl" onClick={e => e.stopPropagation()}>
            <div className="p-4 bg-gray-50 dark:bg-dark-700 border-b border-gray-100 flex justify-between items-center">
              <span className="font-bold text-gray-600">Preview: {previewData.templateName}</span>
              <button onClick={() => setShowPreview(false)}><FaTimes /></button>
            </div>
            <div className="p-8 bg-gray-50 dark:bg-dark-900 overflow-y-auto max-h-[70vh]">
              <div className="bg-white dark:bg-dark-800 shadow-xl rounded-lg overflow-hidden border border-gray-200 dark:border-dark-700">
                <div className="p-4 border-b bg-gray-50 dark:bg-dark-700 shadow-sm">
                  <p className="text-xs text-gray-400">Subject:</p>
                  <p className="font-bold text-sm">{previewData.subject}</p>
                </div>
                <div className="p-8 quill-preview" dangerouslySetInnerHTML={{ __html: previewData.body }} />
              </div>
            </div>
          </div>
        </div>,
        document.body
      )}
    </PageLayout>
  );
};

export default EmailTemplates;
